import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, X, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";

import { TYPE_META, BLANK_CONFIG } from "./destinationConstants";
import { buildConfig } from "./destinationHelpers";
import WebhookFields from "./fields/WebhookFields";
import EmailFields from "./fields/EmailFields";
import BearerCrmFields from "./fields/BearerCrmFields";
import ZohoFields from "./fields/ZohoFields";
import PipedriveFields from "./fields/PipedriveFields";
import ZapierFields from "./fields/ZapierFields";
import { LawRulerFields, LeadDocketFields } from "./fields/LegalCrmFields";
import { S3Fields, FtpFields } from "./fields/StorageFields";
import StateAllowlistFields from "@/components/shared/StateAllowlistFields";

const BEARER_CRMS = new Set([
  "crm_salesforce", "crm_litify", "crm_lawmatics", "crm_hubspot",
]);

function TypeSpecificFields({ type, config, setConfig }) {
  if (type === "webhook") return <WebhookFields config={config} setConfig={setConfig} />;
  if (type === "email") return <EmailFields config={config} setConfig={setConfig} />;
  if (BEARER_CRMS.has(type)) return <BearerCrmFields type={type} config={config} setConfig={setConfig} />;
  if (type === "crm_zoho") return <ZohoFields config={config} setConfig={setConfig} />;
  if (type === "crm_pipedrive") return <PipedriveFields config={config} setConfig={setConfig} />;
  if (type === "crm_zapier") return <ZapierFields config={config} setConfig={setConfig} />;
  if (type === "crm_lawruler") return <LawRulerFields config={config} setConfig={setConfig} />;
  if (type === "crm_leaddocket") return <LeadDocketFields config={config} setConfig={setConfig} />;
  if (type === "s3") return <S3Fields config={config} setConfig={setConfig} />;
  if (type === "ftp") return <FtpFields config={config} setConfig={setConfig} />;
  return null;
}

/**
 * Iter-54: Inline "create a delivery destination without leaving the LP editor"
 * modal. Reuses the same field components as the /delivery page.
 *
 * Flow: pick type → fill config → Save →
 *   • toast + close, OR
 *   • stay open, revealing a "Send test lead" button so the buyer confirms
 *     receipt on their side before the LP goes live.
 *
 * `onCreated(destination)` is called with the freshly-saved destination
 * so the parent can auto-select it in the buyer-destination dropdown.
 */
export default function NewDestinationModal({ onClose, onCreated, defaultType = "webhook" }) {
  const [form, setForm] = useState({
    name: "",
    type: defaultType,
    config: { ...BLANK_CONFIG },
    is_active: true,
    retry_policy: { max_attempts: 5 },
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null); // holds the created destination
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const setConfig = (k, v) => setForm((f) => ({ ...f, config: { ...f.config, [k]: v } }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) return toast.error("Give this destination a name");
    setSaving(true);
    try {
      const payload = { ...form, config: buildConfig(form) };
      const r = await api.post("/delivery/destinations", payload);
      setSaved(r.data);
      toast.success(`Destination created — ${r.data.name}`);
      // Auto-select in the parent form right away; user can still hit "Send test"
      // before dismissing.
      onCreated?.(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!saved) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post(`/delivery/destinations/${saved.id}/test`);
      setTestResult({ ok: true, ...r.data });
      toast.success("Test lead dispatched — check the buyer's inbox / endpoint");
    } catch (err) {
      setTestResult({ ok: false, error: err.response?.data?.detail || String(err) });
      toast.error("Test failed — see modal for details");
    } finally {
      setTesting(false);
    }
  };

  const TYPE_LIST = Object.entries(TYPE_META);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto py-10 px-4"
      data-testid="new-dest-modal"
    >
      <div className="bg-white border-4 border-black max-w-3xl w-full shadow-[8px_8px_0_0_#f1b62c]">
        <div className="border-b-2 border-black px-6 py-4 flex items-center gap-2">
          <Plus size={16} weight="bold" />
          <div className="font-chivo font-black uppercase tracking-tighter text-lg">
            New buyer delivery destination
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="new-dest-close"
            className="ml-auto border border-black w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white text-xs"
          ><X size={12} weight="bold" /></button>
        </div>

        {!saved ? (
          <form onSubmit={submit} className="p-5 grid grid-cols-2 gap-4">
            <div className="col-span-2 border border-black bg-amber-50 p-3 text-[11px] leading-relaxed">
              <b>Every submission from this LP is delivered in realtime</b> —
              webhook POST is sub-second, email arrives within a minute, CRM
              pushes go straight to the buyer&apos;s intake. Failed deliveries
              retry on a 30s → 2m → 10m → 1h → 6h ladder.
            </div>

            <div>
              <label className="label-mono">Name</label>
              <input
                data-testid="new-dest-name"
                className="w-full border border-black px-3 py-2 mt-1"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Firm name — Alabama PI"
              />
            </div>

            <div>
              <label className="label-mono">Delivery method</label>
              <select
                data-testid="new-dest-type"
                className="w-full border border-black px-3 py-2 mt-1 text-sm"
                value={form.type}
                onChange={(e) => setForm({
                  ...form, type: e.target.value, config: { ...BLANK_CONFIG },
                })}
              >
                {TYPE_LIST.map(([v, m]) => (
                  <option key={v} value={v}>{m.label} — {m.help}</option>
                ))}
              </select>
            </div>

            <TypeSpecificFields
              type={form.type}
              config={form.config}
              setConfig={setConfig}
            />

            {/* Iter-61 — shared state selector.
                This inline-modal creates a platform-wide destination
                (no client_id here) so we don't scope to a buyer's
                accepted states — admins may pick any supported state. */}
            <StateAllowlistFields
              config={form.config}
              setConfig={setConfig}
              buyerAcceptedStates={null}
            />

            <div className="col-span-2 flex items-center gap-2 pt-3 border-t-2 border-black">
              <button
                type="submit"
                disabled={saving}
                data-testid="new-dest-save"
                className="border-2 border-black bg-emerald-700 text-white px-4 py-2 text-sm uppercase tracking-widest font-bold disabled:opacity-40 hover:bg-emerald-800 inline-flex items-center gap-2"
              >
                <Plus size={12} weight="bold" />
                {saving ? "Saving…" : "Save destination"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="border border-black px-4 py-2 text-sm uppercase tracking-widest hover:bg-black hover:text-white"
              >
                Cancel
              </button>
              <div className="ml-auto text-[10px] text-zinc-500 font-mono uppercase tracking-widest">
                {TYPE_META[form.type]?.label}
              </div>
            </div>
          </form>
        ) : (
          <div className="p-6 space-y-5">
            <div className="border-2 border-emerald-700 bg-emerald-50 p-4 flex items-start gap-3">
              <CheckCircle size={20} weight="fill" className="text-emerald-700 mt-0.5" />
              <div className="flex-1">
                <div className="font-chivo font-black uppercase tracking-tighter text-base">
                  {saved.name} — saved & selected
                </div>
                <div className="text-xs text-zinc-700 mt-1">
                  This destination has been attached to your landing page. Fire
                  a synthetic test lead so the buyer confirms they can receive
                  before you go live.
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={sendTest}
                disabled={testing}
                data-testid="new-dest-send-test"
                className="border-2 border-black bg-black text-white px-4 py-2 text-sm uppercase tracking-widest font-bold disabled:opacity-40 hover:bg-emerald-700 hover:border-emerald-700 inline-flex items-center gap-2"
              >
                <PaperPlaneTilt size={12} weight="bold" />
                {testing ? "Sending…" : "Send test lead now"}
              </button>
              <button
                type="button"
                onClick={onClose}
                data-testid="new-dest-done"
                className="border border-black px-4 py-2 text-sm uppercase tracking-widest hover:bg-black hover:text-white"
              >
                Done
              </button>
            </div>

            {testResult && (
              <div
                data-testid="new-dest-test-result"
                className={
                  "border-2 p-3 text-xs " +
                  (testResult.ok
                    ? "border-emerald-700 bg-emerald-50"
                    : "border-red-700 bg-red-50")
                }
              >
                <div className="font-bold uppercase tracking-widest text-[10px] mb-1">
                  {testResult.ok ? "Delivered" : "Failed"}
                </div>
                <pre className="whitespace-pre-wrap break-all font-mono text-[10px] leading-relaxed">
                  {JSON.stringify(testResult, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
